import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "panel",
    "status",
    "muteButton",
    "leaveButton",
    "error",
    "remoteAudio",
    "timer",
    "liveBadge",
    "buyerName",
    "buyerPhone",
    "fromLine",
    "toLine",
    "transcript",
    "transcriptEmpty",
    "audioHint"
  ]

  static values = {
    url: String,
    completeUrl: String,
    transcriptionTokenUrl: String,
    buyerName: String,
    buyerPhone: String,
    starting: String,
    connected: String,
    ended: String,
    ringing: String,
    mute: String,
    unmute: String,
    leave: String,
    failed: String,
    setupFailed: String,
    buyerBusy: String,
    rejected: String,
    youLabel: String,
    buyerLabel: String,
    captionsHint: String,
    captionsConnecting: String,
    captionsUnavailable: String,
    audioHint: String,
    transcriptionLanguage: String
  }

  connect() {
    this.device = null
    this.client = null
    this.call = null
    this.callId = null
    this.messageId = null
    this.muted = false
    this.completing = false
    this.endingSession = false
    this.skipDisconnectCleanup = false
    this.clientDisconnectTimer = null
    this.timerInterval = null
    this.callStartedAt = null
    this.transcriber = null
    this.interimLines = {}
    this.provider = "telnyx"
    this.shellHome = null
    this.shellHomeNext = null
    this.placingCall = false
    this.callWasActive = false
    this.sessionPinned = false
    this.lastOutcomeSummary = null
    this.lastDialCause = null
  }

  disconnect() {
    if (this.skipDisconnectCleanup || this.sessionPinned) return
    this.clearClientDisconnectTimer()
    this.tearDown({ markComplete: false, hangupCall: false })
    this.restoreShellHome()
  }

  async start(event) {
    event.preventDefault()
    if (this.client || this.call || this.device) return

    this.clearClientDisconnectTimer()
    this.endingSession = false
    this.callWasActive = false
    this.lastOutcomeSummary = null
    this.lastDialCause = null
    this.resetTranscript()
    this.showPanel()
    this.setStatus(this.startingValue)
    this.clearError()
    this.hideLiveBadge()
    this.stopTimer()
    if (this.hasAudioHintTarget) this.audioHintTarget.hidden = true

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        credentials: "same-origin"
      })

      const payload = await response.json()
      if (!response.ok) {
        throw new Error(payload.error || this.failedValue)
      }

      this.messageId = payload.messageId
      this.provider = payload.provider || "telnyx"
      this.updateCallMeta(payload.from, payload.to)

      if (this.provider === "twilio") {
        await this.startTwilioCall(payload)
      } else {
        await this.startTelnyxCall(payload)
      }
    } catch (error) {
      this.lastOutcomeSummary = error.message || this.failedValue
      this.showError(this.lastOutcomeSummary)
      this.setStatus(this.endedValue)
      this.tearDown({
        markComplete: false,
        hangupCall: false,
        disconnectClient: !this.callId && !this.call
      })
      this.resetSessionState()
    }
  }

  async startTwilioCall(payload) {
    const { Device } = await import("@twilio/voice-sdk")
    this.device = new Device(payload.token, {
      codecPreferences: ["opus", "pcmu"]
    })

    this.device.on("error", (error) => {
      this.showError(error.message || this.failedValue)
      this.setStatus(this.endedValue)
    })

    await this.device.register()

    this.call = await this.device.connect({
      params: {
        InquiryId: String(payload.inquiryId),
        MessageId: String(payload.messageId)
      }
    })

    this.call.on("ringing", () => this.setStatus(this.ringingValue))
    this.call.on("accept", () => this.onCallActive())
    this.call.on("disconnect", () => this.handleDisconnect())
    this.call.on("error", (error) => {
      this.showError(error.message || this.failedValue)
      this.handleDisconnect()
    })
  }

  async startTelnyxCall(payload) {
    const { TelnyxRTC } = await import("@telnyx/webrtc")
    this.client = new TelnyxRTC({ login_token: payload.loginToken })

    if (this.hasRemoteAudioTarget) {
      this.client.remoteElement = this.remoteAudioTarget
    }

    this.client.on("telnyx.error", (event) => {
      if (this.endingSession || this.completing) return

      this.showError(this.telnyxErrorMessage(event))
      this.setStatus(this.endedValue)
    })

    this.client.on("telnyx.notification", (notification) => {
      this.handleTelnyxNotification(notification)
    })

    await this.ensureMicrophoneAccess()
    await this.waitForTelnyxReady()

    const clientState = btoa(JSON.stringify({
      inquiryId: payload.inquiryId,
      messageId: payload.messageId
    }))

    this.placingCall = true
    try {
      const call = this.client.newCall({
        destinationNumber: payload.to,
        callerNumber: payload.from,
        clientState,
        customHeaders: [
          { name: "X-PH-Inquiry-Id", value: String(payload.inquiryId) },
          { name: "X-PH-Message-Id", value: String(payload.messageId) }
        ],
        remoteElement: this.hasRemoteAudioTarget ? this.remoteAudioTarget : undefined,
        audio: true,
        video: false
      })
      this.call = call
      this.callId = call?.id
      this.setStatus(this.ringingValue)
    } finally {
      this.placingCall = false
    }
  }

  async ensureMicrophoneAccess() {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error(this.failedValue)
    }

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    stream.getTracks().forEach((track) => track.stop())
  }

  waitForTelnyxReady() {
    return new Promise((resolve, reject) => {
      let settled = false
      let pollTimer = null
      let timeoutTimer = null

      const finish = (handler, value) => {
        if (settled) return
        settled = true
        cleanup()
        handler(value)
      }

      const onReady = () => finish(resolve)
      const onError = (event) => finish(reject, new Error(this.telnyxErrorMessage(event)))

      const cleanup = () => {
        if (timeoutTimer) window.clearTimeout(timeoutTimer)
        if (pollTimer) window.clearInterval(pollTimer)
        this.client?.off?.("telnyx.ready", onReady)
        this.client?.off?.("telnyx.error", onError)
      }

      const checkRegistered = async () => {
        if (typeof this.client?.getIsRegistered !== "function") return false

        try {
          return await this.client.getIsRegistered()
        } catch (_error) {
          return false
        }
      }

      this.client.on("telnyx.ready", onReady)
      this.client.on("telnyx.error", onError)

      timeoutTimer = window.setTimeout(() => {
        onError({ message: this.failedValue })
      }, 30000)

      checkRegistered().then((registered) => {
        if (registered) onReady()
      })

      pollTimer = window.setInterval(() => {
        checkRegistered().then((registered) => {
          if (registered) onReady()
        })
      }, 500)

      Promise.resolve(this.client.connect()).catch((error) => onError({ error }))
    })
  }

  telnyxErrorMessage(event) {
    return event?.error?.message || event?.message || this.failedValue
  }

  telnyxCauseMessage(call) {
    const cause = (call?.cause || call?.hangupCause || call?.sipCause || "").toString().toUpperCase()
    const prevState = (call?.prevState || "").toString().toLowerCase()
    const detail = call?.causeCode || call?.sipStatus || call?.sipStatusText
    const detailSuffix = detail ? ` (${detail})` : ""

    if (cause === "USER_BUSY" && prevState !== "active") {
      return `${this.hasSetupFailedValue ? this.setupFailedValue : this.failedValue}${detailSuffix}`
    }
    if (cause === "USER_BUSY") {
      return `${this.hasBuyerBusyValue ? this.buyerBusyValue : this.failedValue}${detailSuffix}`
    }
    if (cause === "CALL_REJECTED") {
      return `${this.hasRejectedValue ? this.rejectedValue : this.failedValue}${detailSuffix}`
    }
    if (cause) return `${cause.replace(/_/g, " ")}${detailSuffix}`

    return this.hasSetupFailedValue ? this.setupFailedValue : this.failedValue
  }

  handleTelnyxNotification(notification) {
    const call = notification.call
    if (!call) return

    if (!this.callId) {
      this.callId = call.id
      this.call = call
    } else if (call.id !== this.callId) {
      return
    }

    if (notification.type !== "callUpdate") return

    if (call.state === "ringing") {
      this.setStatus(this.ringingValue)
    }

    if (call.state === "active") {
      this.onCallActive()
      this.ensureRemoteAudioPlaying()
    }

    if (["hangup", "destroy", "destroyed", "done", "purge"].includes(call.state)) {
      if (this.placingCall) return

      if (call.state !== "active" && call.prevState !== "active") {
        this.lastOutcomeSummary = this.telnyxCauseMessage(call)
        this.lastDialCause = (call?.cause || call?.hangupCause || call?.sipCause || "").toString()
        this.showError(this.lastOutcomeSummary)
      }
      this.handleDisconnect({ wasActive: call.prevState === "active" || this.callWasActive })
    }
  }

  onCallActive() {
    this.callWasActive = true
    this.setStatus(this.connectedValue)
    this.showLiveBadge()
    this.startTimer()
    void this.startLiveTranscriptionWhenReady()
    this.ensureRemoteAudioPlaying()
  }

  async ensureRemoteAudioPlaying() {
    if (!this.hasRemoteAudioTarget) return

    const stream = this.call?.remoteStream
    if (stream && this.remoteAudioTarget.srcObject !== stream) {
      this.remoteAudioTarget.srcObject = stream
    }

    try {
      await this.remoteAudioTarget.play()
      if (this.hasAudioHintTarget) this.audioHintTarget.hidden = true
    } catch (_error) {
      if (this.hasAudioHintTarget) this.audioHintTarget.hidden = false
    }
  }

  mediaStreamsForTranscription() {
    const agentStream = this.call?.localStream || null
    const buyerStream = this.call?.remoteStream
      || this.remoteAudioTarget?.srcObject
      || null

    return { agentStream, buyerStream }
  }

  async startLiveTranscriptionWhenReady() {
    if (!this.hasTranscriptionTokenUrlValue) return

    this.setTranscriptEmptyMessage(this.captionsConnectingValue)

    for (let attempt = 0; attempt < 40; attempt += 1) {
      const { agentStream, buyerStream } = this.mediaStreamsForTranscription()
      if (agentStream) {
        await this.startLiveTranscription({ agentStream, buyerStream })
        return
      }
      await new Promise((resolve) => window.setTimeout(resolve, 250))
    }

    this.setTranscriptEmptyMessage(this.captionsUnavailableValue)
  }

  async startLiveTranscription({ agentStream, buyerStream }) {
    if (!this.hasTranscriptionTokenUrlValue) return

    this.stopLiveTranscription()

    try {
      const { BrowserCallTranscriber } = await import("../pro/browser_call_transcriber.js")
      this.transcriber = new BrowserCallTranscriber({
        tokenUrl: this.transcriptionTokenUrlValue,
        language: this.transcriptionLanguage(),
        csrfToken: this.csrfToken,
        onLine: ({ speaker, text, final }) => {
          const label = speaker === "agent" ? this.youLabelValue : this.buyerLabelValue
          if (final) {
            this.clearInterimLine(speaker)
            this.appendTranscriptLine(label, text, { final: true, speaker })
          } else {
            this.updateInterimLine(speaker, label, text)
          }
        },
        onError: () => {
          // Optional captions; ignore stream errors during a call.
        }
      })

      await this.transcriber.start({ agentStream, buyerStream })
    } catch (error) {
      this.setTranscriptEmptyMessage(error.message || this.captionsUnavailableValue)
    }
  }

  setTranscriptEmptyMessage(message) {
    if (!this.hasTranscriptEmptyTarget || !message) return

    this.transcriptEmptyTarget.textContent = message
    this.transcriptEmptyTarget.hidden = false
  }

  transcriptionLanguage() {
    if (this.hasTranscriptionLanguageValue && this.transcriptionLanguageValue) {
      return this.transcriptionLanguageValue
    }

    const lang = document.documentElement.lang?.toLowerCase()
    return lang === "pl" ? "pl" : "en"
  }

  stopLiveTranscription() {
    if (this.transcriber) {
      this.transcriber.stop()
      this.transcriber = null
    }

    this.clearInterimLines()
  }

  transcriptText() {
    if (this.transcriber) {
      const text = this.transcriber.transcriptText({
        agentLabel: this.youLabelValue,
        buyerLabel: this.buyerLabelValue
      })
      if (text) return text
    }

    return this.transcriptFromDom()
  }

  transcriptFromDom() {
    if (!this.hasTranscriptTarget) return ""

    const lines = []
    this.transcriptTarget
      .querySelectorAll(".pro-browser-call__line:not(.pro-browser-call__line--interim)")
      .forEach((node) => {
        const label = node.querySelector(".pro-browser-call__line-label")?.textContent?.trim()
        const clone = node.cloneNode(true)
        clone.querySelector(".pro-browser-call__line-label")?.remove()
        const text = clone.textContent.trim()
        if (label && text) lines.push(`${label}: ${text}`)
      })

    if (lines.length > 0) return lines.join("\n")

    this.transcriptTarget
      .querySelectorAll(".pro-browser-call__line--interim")
      .forEach((node) => {
        const label = node.querySelector(".pro-browser-call__line-label")?.textContent?.trim()
        const clone = node.cloneNode(true)
        clone.querySelector(".pro-browser-call__line-label")?.remove()
        const text = clone.textContent.trim()
        if (label && text) lines.push(`${label}: ${text}`)
      })

    return lines.join("\n")
  }

  speechLocale() {
    const lang = document.documentElement.lang?.toLowerCase()
    return lang === "pl" ? "pl-PL" : "en-US"
  }

  stopLiveCaptions() {
    this.stopLiveTranscription()
  }

  appendTranscriptLine(label, text, { final = true, speaker = "agent" } = {}) {
    if (!this.hasTranscriptTarget || !text) return

    if (this.hasTranscriptEmptyTarget) {
      this.transcriptEmptyTarget.hidden = true
    }

    const line = document.createElement("p")
    line.className = `pro-browser-call__line pro-browser-call__line--${speaker}${final ? "" : " pro-browser-call__line--interim"}`
    line.innerHTML = `<span class="pro-browser-call__line-label">${this.escapeHtml(label)}</span> ${this.escapeHtml(text)}`
    this.transcriptTarget.appendChild(line)
    this.transcriptTarget.scrollTop = this.transcriptTarget.scrollHeight
  }

  updateInterimLine(speaker, label, text) {
    if (!this.hasTranscriptTarget) return

    if (!text) {
      this.clearInterimLine(speaker)
      return
    }

    if (!this.interimLines[speaker]) {
      const line = document.createElement("p")
      line.className = `pro-browser-call__line pro-browser-call__line--${speaker} pro-browser-call__line--interim`
      this.transcriptTarget.appendChild(line)
      this.interimLines[speaker] = line
    }

    this.interimLines[speaker].innerHTML = `<span class="pro-browser-call__line-label">${this.escapeHtml(label)}</span> ${this.escapeHtml(text)}`
    this.transcriptTarget.scrollTop = this.transcriptTarget.scrollHeight
  }

  clearInterimLine(speaker) {
    const line = this.interimLines[speaker]
    if (!line) return

    line.remove()
    delete this.interimLines[speaker]
  }

  clearInterimLines() {
    Object.keys(this.interimLines).forEach((speaker) => this.clearInterimLine(speaker))
  }

  resetTranscript() {
    if (!this.hasTranscriptTarget) return

    this.transcriptTarget.querySelectorAll(".pro-browser-call__line").forEach((node) => node.remove())
    this.interimLines = {}

    if (this.hasTranscriptEmptyTarget) {
      this.transcriptEmptyTarget.textContent = this.captionsHintValue
      this.transcriptEmptyTarget.hidden = false
    }
  }

  updateCallMeta(from, to) {
    if (this.hasFromLineTarget) this.fromLineTarget.textContent = from || "—"
    if (this.hasToLineTarget) this.toLineTarget.textContent = to || this.buyerPhoneValue
    if (this.hasBuyerPhoneTarget && to) this.buyerPhoneTarget.textContent = to
  }

  toggleMute() {
    if (!this.call) return

    this.muted = !this.muted
    if (this.provider === "telnyx") {
      this.call.muteAudio(this.muted)
    } else {
      this.call.mute(this.muted)
    }

    if (this.hasMuteButtonTarget) {
      this.muteButtonTarget.textContent = this.muted ? this.unmuteValue : this.muteValue
    }
  }

  leave(event) {
    event?.preventDefault()
    this.leaveSession()
    this.hidePanel()
  }

  leaveSession() {
    this.tearDown({ markComplete: true })
  }

  handleDisconnect({ wasActive = this.callWasActive } = {}) {
    this.tearDown({ markComplete: wasActive, hangupCall: false, disconnectClient: true })
  }

  tearDown({ markComplete = false, hangupCall = true, disconnectClient = true } = {}) {
    if (this.endingSession) return
    this.endingSession = true

    const transcriptSnapshot = this.transcriptText()
    this.stopLiveTranscription()
    this.stopTimer()
    this.hideLiveBadge()

    const activeCall = this.call
    this.call = null
    this.callId = null

    if (activeCall && hangupCall && !this.telnyxCallEnded(activeCall)) {
      try {
        if (this.provider === "telnyx") {
          activeCall.hangup()
        } else {
          activeCall.disconnect()
        }
      } catch (_error) {
        // Call may already be gone.
      }
    }

    if (this.device) {
      this.device.destroy()
      this.device = null
    }

    if (disconnectClient) {
      this.scheduleClientDisconnect(activeCall)
    } else {
      this.client = null
    }

    this.muted = false
    this.setStatus(this.endedValue)
    if (markComplete) this.clearError()

    if (this.messageId) {
      void this.persistCallOutcome(transcriptSnapshot)
    }

    this.resetSessionState()
  }

  resetSessionState() {
    this.endingSession = false
    this.sessionPinned = false
    this.callWasActive = false
  }

  telnyxCallEnded(call) {
    return ["hangup", "destroy", "destroyed", "done", "purge"].includes(call?.state)
  }

  scheduleClientDisconnect(endedCall = null) {
    this.clearClientDisconnectTimer()
    if (!this.client) return

    const client = this.client
    this.client = null

    if (endedCall && this.telnyxCallEnded(endedCall)) {
      window.setTimeout(() => {
        try {
          client.disconnect()
        } catch (_error) {
          // Socket may already be closed.
        }
      }, 0)
      return
    }

    this.clientDisconnectTimer = window.setTimeout(() => {
      this.clientDisconnectTimer = null
      try {
        client.disconnect()
      } catch (_error) {
        // Socket may already be closed after a remote hangup.
      }
    }, 400)
  }

  clearClientDisconnectTimer() {
    if (!this.clientDisconnectTimer) return

    window.clearTimeout(this.clientDisconnectTimer)
    this.clientDisconnectTimer = null
  }

  startTimer() {
    this.stopTimer()
    this.callStartedAt = Date.now()
    if (this.hasTimerTarget) this.timerTarget.hidden = false
    this.updateTimer()
    this.timerInterval = window.setInterval(() => this.updateTimer(), 1000)
  }

  stopTimer() {
    if (this.timerInterval) {
      window.clearInterval(this.timerInterval)
      this.timerInterval = null
    }
    this.callStartedAt = null
    if (this.hasTimerTarget) this.timerTarget.hidden = true
  }

  updateTimer() {
    if (!this.hasTimerTarget || !this.callStartedAt) return

    const elapsed = Math.floor((Date.now() - this.callStartedAt) / 1000)
    const minutes = String(Math.floor(elapsed / 60)).padStart(2, "0")
    const seconds = String(elapsed % 60).padStart(2, "0")
    this.timerTarget.textContent = `${minutes}:${seconds}`
  }

  showLiveBadge() {
    if (this.hasLiveBadgeTarget) this.liveBadgeTarget.hidden = false
  }

  hideLiveBadge() {
    if (this.hasLiveBadgeTarget) this.liveBadgeTarget.hidden = true
  }

  async persistCallOutcome(transcript = "") {
    if (this.completing || !this.messageId || !this.hasCompleteUrlValue) return

    this.completing = true
    const durationSeconds = this.callStartedAt
      ? Math.floor((Date.now() - this.callStartedAt) / 1000)
      : 0
    const transcriptText = transcript || this.transcriptText()

    try {
      await fetch(this.completeUrlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": this.csrfToken
        },
        credentials: "same-origin",
        body: new URLSearchParams({
          message_id: this.messageId,
          connected: this.callWasActive ? "1" : "0",
          duration_seconds: String(durationSeconds),
          outcome_summary: this.lastOutcomeSummary || "",
          cause: this.lastDialCause || "",
          transcript: transcriptText
        })
      })
      this.skipDisconnectCleanup = true
      this.refreshThread()
    } catch (_error) {
      // Webhook fallback will still mark the thread entry completed.
    }
  }

  refreshThread() {
    if (window.Turbo) {
      window.Turbo.visit(window.location.href, { action: "replace" })
      return
    }

    window.location.reload()
  }

  showPanel() {
    this.portalShell()
    this.sessionPinned = true
    this.setStartButtonHidden(true)
    if (this.hasPanelTarget) this.panelTarget.hidden = false
    this.element.closest("details")?.removeAttribute("open")
  }

  hidePanel() {
    if (this.hasPanelTarget) this.panelTarget.hidden = true
    this.setStartButtonHidden(false)
    this.restoreShellHome()
  }

  portalShell() {
    if (this.element.parentElement === document.body) return

    this.element.dataset.turboPermanent = ""
    this.shellHome = this.element.parentElement
    this.shellHomeNext = this.element.nextSibling
    document.body.appendChild(this.element)
  }

  restoreShellHome() {
    if (!this.shellHome || this.element.parentElement !== document.body) return

    if (this.shellHomeNext && this.shellHomeNext.parentElement === this.shellHome) {
      this.shellHome.insertBefore(this.element, this.shellHomeNext)
    } else {
      this.shellHome.appendChild(this.element)
    }

    this.shellHome = null
    this.shellHomeNext = null
  }

  setStartButtonHidden(hidden) {
    const button = this.element.querySelector('[data-action~="pro-browser-call#start"]')
    if (button) button.hidden = hidden
  }

  setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  showError(message) {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  clearError() {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = ""
    this.errorTarget.hidden = true
  }

  escapeHtml(text) {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
